import { Bookmark, BookmarkCheck, ChevronRight } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
    'bg-violet-100 text-violet-700',
    'bg-blue-100 text-blue-700',
    'bg-emerald-100 text-emerald-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
    'bg-teal-100 text-teal-700',
    'bg-amber-100 text-amber-700',
    'bg-indigo-100 text-indigo-700',
    'bg-rose-100 text-rose-700',
    'bg-cyan-100 text-cyan-700',
];

export const getAvatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const getDaysAgo = (dateStr) => {
    if (!dateStr) return null;
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
};

export const formatEmployeeCount = (size) => {
    if (!size && size !== 0) return null;
    if (typeof size === 'string' && size.includes('_')) {
        const parts = size.split('_').map(n => parseInt(n)).filter(n => !isNaN(n));
        if (parts.length === 2) {
            const fmt = n => n >= 1000 ? `${(n / 1000) % 1 === 0 ? n / 1000 : (n / 1000).toFixed(1)}K` : String(n);
            return `${fmt(parts[0])}–${fmt(parts[1])}`;
        }
    }
    const n = typeof size === 'string' ? parseInt(size.replace(/[^0-9]/g, '')) : size;
    if (isNaN(n) || n === 0) return null;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000) % 1 === 0 ? n / 1000 : (n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
};

export const getCompanySizeTag = (size) => {
    if (!size && size !== 0) return null;
    let numSize;
    if (typeof size === 'string') {
        numSize = size.includes('_') ? parseInt(size.split('_')[0]) : parseInt(size.replace(/[^0-9]/g, ''));
    } else {
        numSize = size;
    }
    if (isNaN(numSize) || numSize === 0) return null;
    if (numSize <= 10)   return { label: 'Micro',      color: 'bg-pink-100 text-pink-700' };
    if (numSize <= 50)   return { label: 'Startup',    color: 'bg-purple-100 text-purple-700' };
    if (numSize <= 200)  return { label: 'Small',      color: 'bg-blue-100 text-blue-700' };
    if (numSize <= 1000) return { label: 'Growing',    color: 'bg-green-100 text-green-700' };
    if (numSize <= 5000) return { label: 'Mid-size',   color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Enterprise', color: 'bg-sky-100 text-sky-700' };
};

export const renderScoreBadge = (score, activeColor, mutedColor) => {
    if (score == null) return <span className="text-gray-300 text-[11px]">—</span>;
    const cls = score >= 8 ? activeColor : score >= 6 ? mutedColor : 'bg-gray-100 text-gray-500';
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-md text-[12px] font-bold ${cls}`}>
            {score}/10
        </span>
    );
};

/**
 * Resolve the hiring-signal score a card should display.
 *
 * Prefers `evidence.confidenceScore` — derived from counted independent sources
 * and verified job postings — over `scores.hiring_signal_score`, which is the
 * model's own assessment of prose it wrote in the same request.
 *
 * Returns `score: null` when there is genuinely no evidence. Null must render as
 * "Insufficient data", never as a number: a fabricated middling score is what
 * made every unknown company look moderately promising.
 */
export const resolveSignalConfidence = (signal) => {
    const evidence = signal?.evidence || null;

    if (evidence && evidence.confidenceScore !== undefined) {
        return {
            score: evidence.confidenceScore,
            sourceCount: evidence.sourceCount || 0,
            hasLiveJob: evidence.hasLiveJob === true,
            label: evidence.confidenceLabel || null,
            reason: evidence.reason || null,
            // Backend-authored display text, e.g. "Not hiring" when every posting
            // was confirmed closed. Distinguishing that from "we have no data" is
            // the difference between a useful card and a blank one.
            display: evidence.display || null,
            isEvidenceBased: true
        };
    }

    // Pre-`evidence` documents: fall back to the legacy score, flagged so the UI
    // does not claim source-verified confidence it cannot support.
    const legacy = signal?.scores?.hiring_signal_score;
    return {
        score: legacy === undefined ? null : legacy,
        sourceCount: 0,
        hasLiveJob: false,
        label: null,
        reason: null,
        isEvidenceBased: false
    };
};

/**
 * Hiring-signal cell: score plus the corroboration behind it.
 *
 * The source count is the trust-earning part. "3 independent signals" tells a
 * user why the number is believable in a way the number alone never does.
 */
/**
 * Overall = hiring signal + candidate fit, averaged.
 *
 * The hiring half must be the EVIDENCE score, not the model's self-reported one.
 * Mixing them produced rows like "Hiring Signal 3/10 · Overall 6/10" — Overall
 * higher than the signal it summarises, because it was averaging the model's
 * optimistic 4 with a fit of 8 while the column beside it showed verified
 * evidence of 3. One row, two scoring systems, and the more trustworthy number
 * looked like the worse one.
 *
 * Returns null when either half is unknown — a half-known average is not a score.
 */
export const resolveOverallScore = (signal) => {
    const { score, isEvidenceBased } = resolveSignalConfidence(signal);

    // Pre-`evidence` documents keep their stored overall; there is no verified
    // hiring score to substitute in.
    if (!isEvidenceBased) {
        const legacy = signal?.scores?.overall_score;
        return legacy === undefined ? null : legacy;
    }

    if (score == null) return null;

    const fit = signal?.scores?.skill_fit_score;
    if (typeof fit !== 'number') return null;

    return Number(((score + fit) / 2).toFixed(1));
};

export const renderConfidenceCell = (signal) => {
    const { score, sourceCount, hasLiveJob, label, display, isEvidenceBased } = resolveSignalConfidence(signal);

    if (score == null) {
        // Every posting verified closed. "Not hiring" reads as a dead end and as a
        // wasted credit, but the underlying fact is more useful than that: this
        // company WAS hiring days ago, the roles are now filled, and the hiring
        // manager and recruiter are still there. That is a warm outreach lead, not
        // a null result — so name what happened rather than what isn't happening.
        const rolesFilled = display === 'Not hiring';
        return (
            <div className="flex flex-col gap-0.5">
                <span className={`text-[11px] font-semibold ${rolesFilled ? 'text-amber-600' : 'text-gray-400'}`}>
                    {rolesFilled ? 'Roles filled' : (isEvidenceBased && display ? display : '—')}
                </span>
                {rolesFilled && (
                    <span className="text-[10px] text-gray-500 whitespace-nowrap">hired recently</span>
                )}
            </div>
        );
    }

    const cls = label === 'mixed'
        ? 'bg-amber-100 text-amber-700'
        : score >= 8
            ? 'bg-blue-100 text-blue-700'
            : score >= 6
                ? 'bg-blue-50 text-blue-500'
                : 'bg-gray-100 text-gray-500';

    return (
        <div className="flex flex-col gap-0.5">
            <span className={`inline-block px-2.5 py-0.5 rounded-md text-[12px] font-bold w-fit ${cls}`}>
                {score}/10
            </span>
            {/* Proof first, count second.
                "1 signal" reads as a shortfall on almost every row, and "verified"
                never said what was verified. A confirmed-open role is the strongest
                thing we know and the thing nobody else checks, so it leads. The
                source count only appears at 2+, where it is genuinely a strength. */}
            {isEvidenceBased && hasLiveJob && (
                <span className="text-[10px] text-emerald-600 font-medium whitespace-nowrap">
                    ✓ role open now
                </span>
            )}
            {isEvidenceBased && sourceCount >= 2 && (
                <span className="text-[10px] text-gray-500 whitespace-nowrap">
                    + {sourceCount - 1} more {sourceCount === 2 ? 'source' : 'sources'}
                </span>
            )}
            {/* Deliberately no subtitle when nothing is verified.
                "1 source · unconfirmed" was developer language: it read as an
                apology and told the user nothing they could act on. The score
                already carries that meaning — 3/10 says "weak" on its own. The
                positive markers above stay, because those DO add information. */}
            {/* Pre-`evidence` signals: the number is the model's own assessment,
                never corroborated against a job board. Saying so is the difference
                between an honest legacy score and a falsely confident one. */}
            {!isEvidenceBased && (
                <span className="text-[10px] text-gray-400 whitespace-nowrap">unverified</span>
            )}
            {label === 'mixed' && (
                <span className="text-[10px] text-amber-600 whitespace-nowrap">Mixed signals</span>
            )}
        </div>
    );
};

export const renderCompanyAvatar = (logo, companyName) => {
    const safeLogo = logo && !logo.includes('thenounproject') && !logo.includes('default') ? logo : null;
    if (safeLogo) {
        return (
            <img
                src={safeLogo}
                alt={companyName}
                className="w-7 h-7 rounded-lg border border-gray-200 object-cover bg-white shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
            />
        );
    }
    return (
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${getAvatarColor(companyName)}`}>
            {(companyName || 'C').charAt(0).toUpperCase()}
        </div>
    );
};

export const renderBookmark = ({ isSaved, isSaving, onToggle }) => (
    <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        disabled={isSaving}
        className={`shrink-0 p-0.5 rounded transition-colors ${isSaved ? 'text-[#3F57E0]' : 'text-gray-300 hover:text-gray-500'}`}
    >
        {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
    </button>
);

export const renderViewDetailsBtn = (onClick) => (
    <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-[#3F57E0] border border-[#3F57E0] rounded-md hover:bg-[#EEF1FE] transition-colors whitespace-nowrap"
    >
        View Details <ChevronRight className="w-3 h-3" />
    </button>
);
